import { useCallback } from "react";
import { ReportColumn, Report as ReportType } from "/app/src/models";
import { useQueryClient } from "@tanstack/react-query";
import { reportService } from "/app/src/services";
import { Formik, FormikProps } from "formik";
import { Col, Row } from "antd";
import { Form, SubmitButton } from "formik-antd";
import { useTranslation } from "react-i18next";
import GroupingColumnFormItems from "./groupingColumnFormItems";

interface FormValues {
  groupColumnId: number | undefined;
  subGroupColumnId: number | undefined;
  finalGroupColumnId: number | undefined;
  groupRange?: "minute" | "hour" | "day" | "week" | "month" | "year";
}

/**
 * Component to set the grouping columns for the report
 */
export default function GroupingColumns({
  report,
  columns,
}: {
  report: ReportType;
  columns: ReportColumn[];
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const onSubmitHandler = useCallback(
    (values: FormValues) => {
      return reportService
        .updateSingle(report.id, {
          groupColumnId: values.groupColumnId ? values.groupColumnId : null,
          subGroupColumnId: values.subGroupColumnId
            ? values.subGroupColumnId
            : null,
          finalGroupColumnId: values.finalGroupColumnId
            ? values.finalGroupColumnId
            : null,
          groupRange: values.groupRange,
        })
        .then(() => {
          queryClient.setQueryData(["report", report.id], {
            report: {
              ...report,
              groupColumnId: values.groupColumnId,
              subGroupColumnId: values.subGroupColumnId,
              finalGroupColumnId: values.finalGroupColumnId,
              groupRange: values.groupRange,
            },
          });
        });
    },
    [report, queryClient],
  );

  const columnForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ isSubmitting, dirty }) => (
        <Form>
          <Row gutter={16}>
            <GroupingColumnFormItems columns={columns} />
            <Col span={3}>
              <SubmitButton
                type="primary"
                size="large"
                block
                disabled={!dirty || isSubmitting}
              >
                {t("translation:save")}
              </SubmitButton>
            </Col>
          </Row>
        </Form>
      ),
      [columns, t],
    );

  return (
    <div className="groupingColumns">
      <h2>{t("translation:column_grouping")}</h2>
      <Formik
        component={columnForm}
        enableReinitialize
        initialValues={{
          groupColumnId: report.groupColumnId,
          subGroupColumnId: report.subGroupColumnId,
          finalGroupColumnId: report.finalGroupColumnId,
          groupRange: report.groupRange,
        }}
        onSubmit={onSubmitHandler}
      />
    </div>
  );
}
